import { useEffect, useRef, useCallback } from 'react';
import {
  DragHelper,
  DomHelper,
  DateHelper,
  Grid,
  SchedulerPro,
  Model,
  ResourceModel,
  ScrollManager,
  DragHelperConfig,
  Tooltip,
} from '@bryntum/schedulerpro';
import { addMinutes, isSameDay, isWithinInterval } from 'date-fns';
import { format } from 'date-fns-tz';
import { getTimeWithOffset } from '@dispatch/shared';
import { PendingSchedulerEvent } from '../types/scheduler.types';
import { escapeHtml } from '../utils/escapeHtml';

// Bryntum-internal CSS classes used by the resourceNonWorkingTime and
// nonWorkingTime features. These are not part of Bryntum's public API
// and could be renamed on a major upgrade — used only as a fallback for
// when isDateRangeAvailable misses calendar-driven non-working blocks.
const NON_WORKING_TIME_SELECTOR =
  '.b-sch-resource-non-working-time, .b-sch-nonworkingtime';

// Format time for display (e.g., "2:30 PM")
function formatTimeForDisplay(date: Date, timeZone?: string): string {
  return format(date, 'h:mm a', { timeZone });
}

interface DropParams {
  taskId: string;
  resourceId: string;
  startDate: string;
  endDate: string;
}

interface UseDragDropOptions {
  schedulerRef: React.RefObject<{ instance?: SchedulerPro }>;
  gridRef: React.RefObject<{ instance?: Grid }>;
  timeZone: string;
  onDrop: (params: DropParams) => Promise<void>;
  enabled?: boolean;
  onPendingEventChange?: (event: PendingSchedulerEvent | null) => void;
  onInvalidDrop?: (message: string) => void;
}

type SchedulerDrag = SchedulerPro & {
  isHorizontal?: boolean;
  allowOverlap?: boolean;
  unhighlightTimeSpans?: (value?: boolean) => void;
  highlightTimeSpans?: (
    values: {
      name: string;
      cls: string;
      startDate: Date;
      endDate: Date;
    }[]
  ) => void;
  rowHeight?: number;
  resourceMargin?: number;
  mode?: string;
  timeAxisViewModel?: {
    getDistanceForDuration: (ms: number) => number;
  };
  getDateFromCoordinate?: (
    x: number,
    roundingMethod?: string,
    local?: boolean
  ) => Date | null;
  getCoordinateFromDate?: (date: Date, local?: boolean) => number;
  resolveResourceRecord?: (
    element: HTMLElement,
    coordinates?: [number, number]
  ) => DragResource | null;
  resolveEventRecord?: (element: HTMLElement) => unknown;
  scrollToDate?: (date: Date, options?: { block?: string }) => void;
  scrollManager?: ScrollManager;
  features?: {
    eventTooltip?: {
      disabled?: boolean;
    };
  };
  timeAxisSubGrid?: HTMLElement;
  enableScrollingCloseToEdges?: (element: HTMLElement) => void;
  disableScrollingCloseToEdges?: (element: HTMLElement) => void;
};

interface DragResource {
  data?: {
    id: string;
  };
}

type DragTask = Model & {
  data?: {
    appointmentDurationInMinutes?: number;
    appointment?: string | Date;
    insuredName?: string;
    claimNumber?: string;
    extRefId?: string;
    mustStartAfter?: string | Date | null;
    mustEndBefore?: string | Date | null;
  };
};

interface DragTarget {
  className: string;
}

interface DragContext {
  element: HTMLElement;
  grabbed: HTMLElement;
  relatedElements: HTMLElement[];
  clientX?: number;
  elementStartX?: number;
  elementX?: number;
  newX?: number;
  task?: DragTask;
  target?: DragTarget & HTMLElement;
  valid?: boolean;
  startDate?: Date;
  endDate?: Date;
  resource?: DragResource;
  finalize?: () => void;
}

interface DragContextEvent {
  source: DragHelper;
  context: DragContext;
  event: MouseEvent | TouchEvent;
}

interface DropContextEvent {
  source: DragHelper;
  context: DragContext;
}

type DragConfig = Partial<DragHelperConfig> & {
  grid: Grid;
  schedule: SchedulerPro;
  outerElement: HTMLElement;
  timeZone?: string;
  onDropTask: (values: {
    taskId: string;
    resourceId: string;
    dates: { startDate: string; endDate: string };
  }) => void;
  onPendingEventChange?: (event: PendingSchedulerEvent | null) => void;
  onInvalidDrop?: (message: string) => void;
};

// ============================================================================
// Helpers
// ============================================================================

function formatDatesUsingTimeZone(
  date: Date,
  task: DragTask,
  timeZone?: string
): { startDate: string; endDate: string } {
  const startDate = format(new Date(date), 'yyyy-MM-dd HH:mm:ssXXX', {
    timeZone,
  });
  const endDateWithDuration = addMinutes(
    new Date(date),
    task?.data?.appointmentDurationInMinutes || 0
  );
  const endDate = format(endDateWithDuration, 'yyyy-MM-dd HH:mm:ssXXX', {
    timeZone,
  });
  return { startDate, endDate };
}

class Drag extends DragHelper {
  static get configurable() {
    return {
      callOnFunctions: true,
      cloneTarget: true,
      autoSizeClonedTarget: false,
      dropTargetSelector: '.b-timeline-subgrid',
      targetSelector: '.b-grid-row:not(.b-group-row)',
    };
  }

  public grid: Grid;
  public outerElement: HTMLElement;
  private _schedule!: SchedulerDrag;
  private onDropTask: (values: {
    taskId: string;
    resourceId: string;
    dates: { startDate: string; endDate: string };
  }) => void;
  public timeZone?: string;
  private _dragTooltip?: Tooltip;
  private _highlightStartDate?: Date;
  private _highlightEndDate?: Date;
  private _constraintStartDate?: Date;
  private _isValidDrop = false;
  private _lastInvalidReason = '';
  private onPendingEventChange?: (event: PendingSchedulerEvent | null) => void;
  private onInvalidDrop?: (message: string) => void;

  constructor(config: DragConfig) {
    super(config);
    this.grid = config.grid;
    this.schedule = config.schedule;
    this.outerElement = config.outerElement;
    this.onDropTask = config.onDropTask;
    this.timeZone = config.timeZone;
    this.onPendingEventChange = config.onPendingEventChange;
    this.onInvalidDrop = config.onInvalidDrop;
  }

  afterConstruct(): void {
    // Configure DragHelper with schedule's scrollManager to allow scrolling while dragging
    // DragHelper has scrollManager as an internal property for auto-scrolling during drag
    const dragHelperWithScrollManager = this as DragHelper & {
      scrollManager?: ScrollManager;
    };
    dragHelperWithScrollManager.scrollManager = this.schedule
      .scrollManager as ScrollManager;
  }

  override onDragStart = ({ context }: DragContextEvent) => {
    const { _schedule: schedule, grid } = this;
    const mouseX: number = context.clientX || 0;
    const task: DragTask = grid.getRecordFromElement(context.grabbed);
    const durationMinutes = task?.data?.appointmentDurationInMinutes || 60;

    // Save reference to the task
    context.task = task;

    const endDate =
      task?.data?.appointment &&
      DateHelper.add(task?.data?.appointment, durationMinutes, 'minute');

    // Scroll to appointment time (1 hour before)
    if (task?.data?.appointment) {
      const formattedAppointment = new Date(task.data.appointment);
      formattedAppointment.setHours(formattedAppointment.getHours() - 1);
      const scrollToDate = getTimeWithOffset(
        this.timeZone,
        formattedAppointment
      );
      scrollToDate.setMinutes(0);
      schedule.scrollToDate?.(scrollToDate, { block: 'start' });
    }

    // Calculate highlight start/end dates with timezone
    const highlightStartDate = task?.data?.appointment
      ? getTimeWithOffset(this.timeZone, new Date(task.data.appointment))
      : null;
    const highlightEndDate = endDate
      ? getTimeWithOffset(this.timeZone, new Date(endDate))
      : null;

    // Store highlight dates for snap behavior
    this._highlightStartDate = highlightStartDate || undefined;
    this._highlightEndDate = highlightEndDate || undefined;
    this._isValidDrop = false;

    // Calculate constraint window start date if available.
    // End date (mustEndBefore) is intentionally not tracked — it no longer
    // constrains drag validity.
    const constraintStartDate = task?.data?.mustStartAfter
      ? getTimeWithOffset(this.timeZone, new Date(task.data.mustStartAfter))
      : null;

    this._constraintStartDate = constraintStartDate || undefined;

    if (highlightStartDate && highlightEndDate) {
      schedule?.highlightTimeSpans?.([
        {
          name: 'available',
          cls: 'available',
          startDate: highlightStartDate,
          endDate: highlightEndDate,
        },
      ]);
    }

    // Format appointment time for display
    const appointmentTimeStr = highlightStartDate
      ? formatTimeForDisplay(highlightStartDate, this.timeZone)
      : '';

    // Get duration size in px for proxy sizing - use exact same duration as highlight
    const highlightDurationMs =
      highlightStartDate && highlightEndDate
        ? highlightEndDate.getTime() - highlightStartDate.getTime()
        : durationMinutes * 60 * 1000;

    const durationInPixels =
      schedule.timeAxisViewModel?.getDistanceForDuration(highlightDurationMs) ||
      100;

    // Style the proxy element
    const proxy = context.element;
    proxy.style.cssText = '';
    proxy.style.width = `${durationInPixels}px`;
    proxy.style.height = `${(schedule.rowHeight || 50) - 4}px`;
    proxy.classList.remove('b-grid-row');
    proxy.classList.add(
      'b-drag-proxy',
      'b-dragging',
      'b-sch-event-wrap',
      'b-sch-event',
      'b-unassigned-class'
    );
    if (schedule.mode) {
      proxy.classList.add(`b-${schedule.mode}`);
    }
    proxy.innerHTML = `
      <div class="drag-proxy">
        <div class="drag-proxy-title">${escapeHtml(
          task?.data?.insuredName
        )} - ${escapeHtml(task?.data?.claimNumber)}</div>
        ${
          appointmentTimeStr
            ? `<div class="drag-proxy-time">${appointmentTimeStr}</div>`
            : ''
        }
      </div>
    `;

    // Set grabbing cursor on body during drag
    document.body.style.cursor = 'grabbing';

    // Always center the proxy under the mouse cursor for better UX
    context.newX =
      context.elementStartX =
      context.elementX =
        mouseX - durationInPixels / 2;
    DomHelper.setTranslateX(proxy, context.newX);

    // Prevent tooltips from showing while dragging
    schedule.element?.classList.add('b-dragging-event');
    if (schedule.features?.eventTooltip) {
      schedule.features.eventTooltip.disabled = true;
    }

    // Enable edge scrolling
    if (schedule.timeAxisSubGrid) {
      schedule.enableScrollingCloseToEdges?.(schedule.timeAxisSubGrid);
    }

    // Create drag tooltip
    this._dragTooltip = new Tooltip({
      forElement: proxy,
      html: 'Drop on a resource row within the highlighted zone',
      cls: 'drag-drop-tooltip',
      align: 'b-t', // tooltip below, aligned to top of target
      autoShow: true,
    });
  };

  override onDrag = ({ context, event }: DragContextEvent) => {
    const { _schedule: schedule } = this;
    const task: DragTask | undefined = context?.task;
    if (!task) return;

    const coordinate = DomHelper.getTranslateX(context.element);
    const startDate = schedule?.getDateFromCoordinate?.(
      coordinate,
      'round',
      false
    );

    if (!startDate) {
      context.valid = false;
      return;
    }

    const durationMinutes = task?.data?.appointmentDurationInMinutes || 60;
    const endDate = DateHelper.add(startDate, durationMinutes, 'minute');

    // Resolve resource from target element - try with coordinates first
    let resource = null;
    if (context.target) {
      const mouseEvent = event as MouseEvent;
      // Try with coordinates for newer Bryntum versions
      resource = schedule.resolveResourceRecord?.(
        context.target as HTMLElement,
        [mouseEvent.offsetX, mouseEvent.offsetY]
      );
      // Fallback to without coordinates
      if (!resource) {
        resource = schedule.resolveResourceRecord?.(
          context.target as HTMLElement
        );
      }
    }

    // Snap-to-highlight: when proxy is close to the highlight zone, snap to it
    const SNAP_THRESHOLD = 30; // pixels
    let isSnapped = false;
    let effectiveStartDate = startDate;
    let effectiveEndDate = endDate;

    if (this._highlightStartDate && this._highlightEndDate && resource) {
      const highlightX = schedule.getCoordinateFromDate?.(
        this._highlightStartDate,
        false
      );
      if (highlightX !== undefined && highlightX !== null) {
        const currentX = DomHelper.getTranslateX(context.element);
        const distance = Math.abs(currentX - highlightX);
        if (distance < SNAP_THRESHOLD) {
          // Snap to highlight position
          DomHelper.setTranslateX(context.element, highlightX);
          context.element.classList.add('snapped');
          isSnapped = true;
          // Use highlight dates for validation when snapped
          effectiveStartDate = this._highlightStartDate;
          effectiveEndDate = this._highlightEndDate;
        } else {
          context.element.classList.remove('snapped');
        }
      }
    }

    // Get appointment date with timezone offset
    let appointmentDate: Date | null = null;
    if (task?.data?.appointment) {
      appointmentDate = getTimeWithOffset(
        this.timeZone,
        new Date(task.data.appointment)
      );
    }
    context.valid = !!(effectiveStartDate && resource);

    // Calculate validation details
    const isOnEvent = context?.target?.className?.includes('event') || false;
    let appointmentWithinInterval = true;
    let outsideConstraintWindow = false;
    let isNonWorkingTime = false;

    // Check constraint window start bound (mustStartAfter).
    // The end date is intentionally not constrained — users may drag an
    // appointment so that it ends after mustEndBefore; only the start must
    // remain within the allowed window.
    if (this._constraintStartDate && resource) {
      if (effectiveStartDate < this._constraintStartDate) {
        outsideConstraintWindow = true;
        context.valid = false;
      }
    }

    // V1 parity: block drops whose time range hits the resource's
    // non-working calendar. Bryntum's isDateRangeAvailable checks the
    // resource calendar and existing events; an unassigned task has no
    // in-store record, so overlap is handled separately via isOnEvent.
    if (resource && schedule?.isDateRangeAvailable) {
      const available = schedule.isDateRangeAvailable(
        effectiveStartDate as Date,
        effectiveEndDate as Date,
        null,
        resource as unknown as ResourceModel
      );
      if (available === false) {
        isNonWorkingTime = true;
        context.valid = false;
      }
    }

    // DOM-level fallback: isDateRangeAvailable misses some cases (e.g.
    // resource-level non-working time rendered by the resourceNonWorkingTime
    // feature when the calendar isn't wired to that resource). If the
    // drag target is inside a non-working-time element, treat it as
    // non-working so the user gets the right message instead of the
    // generic "Drop within the highlighted zone" fallback.
    const targetEl = context?.target as HTMLElement | undefined;
    if (!isNonWorkingTime && targetEl?.closest?.(NON_WORKING_TIME_SELECTOR)) {
      isNonWorkingTime = true;
      context.valid = false;
    }

    // When snapped, we're always in the valid interval (unless outside
    // constraint or hitting non-working time — snapping must not force a
    // valid state when a hard rule already rejected the drop).
    if (
      isSnapped &&
      !isOnEvent &&
      !outsideConstraintWindow &&
      !isNonWorkingTime
    ) {
      appointmentWithinInterval = true;
      context.valid = true;
    } else if (appointmentDate && durationMinutes && durationMinutes > 120) {
      // For long appointments (> 2 hours), must be on same day
      if (!isSameDay(new Date(effectiveStartDate), appointmentDate)) {
        context.valid = false;
      }
    } else if (
      (appointmentDate &&
        !isWithinInterval(appointmentDate, {
          start: new Date(effectiveStartDate),
          end: new Date(effectiveEndDate),
        })) ||
      isOnEvent
    ) {
      // Appointment must be within the drop window, and can't drop on existing event
      appointmentWithinInterval = false;
      context.valid = false;
    }

    // Save references for drop handler (use effective dates if snapped)
    context.startDate = effectiveStartDate;
    context.endDate = effectiveEndDate;
    context.resource = resource as DragResource;
    context.element.classList.toggle('b-drag-invalid', !context.valid);

    const innerProxy = context.element.querySelector(
      '.drag-proxy'
    ) as HTMLElement;

    if (context.valid) {
      // Valid state - green
      context.element.style.setProperty(
        'outline',
        '2px solid #4caf50',
        'important'
      );
      context.element.style.setProperty('outline-offset', '-2px', 'important');
      context.element.style.setProperty('box-shadow', 'none', 'important');
      document.body.style.cursor = 'grabbing';
      if (innerProxy) {
        innerProxy.style.setProperty(
          'background-color',
          '#c8e6c9',
          'important'
        );
      }
      // Update tooltip for valid state
      if (this._dragTooltip && this._highlightStartDate) {
        const timeStr = formatTimeForDisplay(
          this._highlightStartDate,
          this.timeZone
        );
        this._dragTooltip.html = `Drop to schedule at ${timeStr}`;
      }
    } else {
      // Invalid state - red
      context.element.style.setProperty(
        'outline',
        '2px solid #f44336',
        'important'
      );
      context.element.style.setProperty('outline-offset', '-2px', 'important');
      context.element.style.setProperty('box-shadow', 'none', 'important');
      document.body.style.cursor = 'not-allowed';
      if (innerProxy) {
        innerProxy.style.setProperty(
          'background-color',
          '#ffcdd2',
          'important'
        );
      }
      // Determine invalid reason for tooltip and toast
      let invalidReason = 'Invalid drop location';
      if (!resource) {
        invalidReason = 'Drag over a resource row';
      } else if (isOnEvent) {
        invalidReason = 'Cannot drop on existing event';
      } else if (outsideConstraintWindow) {
        invalidReason = 'Outside allowed time window';
      } else if (isNonWorkingTime) {
        invalidReason = 'Cannot be scheduled on non-working time';
      } else if (!appointmentWithinInterval) {
        invalidReason = 'Drop within the highlighted zone';
      }
      this._lastInvalidReason = invalidReason;

      // Update tooltip for invalid state
      if (this._dragTooltip) {
        this._dragTooltip.html = invalidReason;
      }
    }

    // Store validity for drop handler (context.valid may be cleared by Bryntum)
    this._isValidDrop = context.valid ?? false;
  };

  // Create a pending event via React state (not Bryntum store)
  // This ensures the event persists through React re-renders
  createPendingEvent = (task: DragTask, context: DragContext) => {
    if (!this.onPendingEventChange || !context.startDate || !context.endDate) {
      return;
    }

    const pendingEvent: PendingSchedulerEvent = {
      id: `pending-${task?.data?.extRefId || Date.now()}`,
      // Use claim number as name to match real event display
      name: task?.data?.claimNumber || '',
      startDate: context.startDate,
      endDate: context.endDate,
      resourceId: context.resource?.data?.id || '',
      cls: 'b-sch-event-pending just-dropped',
      isPending: true,
      realTaskId: task?.data?.extRefId || '',
    };

    this.onPendingEventChange(pendingEvent);
  };

  override onDrop = ({ context }: DropContextEvent) => {
    const { _schedule: schedule, grid } = this;
    const task = context.task;
    const target = context.target;

    // Immediately destroy tooltip to prevent it lingering
    if (this._dragTooltip) {
      this._dragTooltip.destroy();
      this._dragTooltip = undefined;
    }

    // Remove highlighting
    schedule?.unhighlightTimeSpans?.(true);

    // Re-resolve resource at drop time
    const resource = target
      ? schedule.resolveResourceRecord?.(target as HTMLElement)
      : context.resource;

    const resourceId =
      (resource as DragResource)?.data?.id || context.resource?.data?.id;

    // Use the task's appointment time, NOT where they dropped
    const appointmentTime = task?.data?.appointment
      ? new Date(task.data.appointment)
      : null;

    const targetEventRecord = schedule.resolveEventRecord?.(
      target as HTMLElement
    );

    // Validate: must be valid drop (green), have appointment, resource, and not on existing event
    if (
      this._isValidDrop &&
      appointmentTime &&
      task &&
      resourceId &&
      target &&
      !targetEventRecord
    ) {
      // Remove from grid first
      (grid.store as { remove: (task: DragTask) => void }).remove(task);

      // Update context with appointment time for pending event
      context.startDate = appointmentTime;
      const durationMinutes = task?.data?.appointmentDurationInMinutes || 60;
      context.endDate = DateHelper.add(
        appointmentTime,
        durationMinutes,
        'minute'
      );
      context.resource = (resource as DragResource) || context.resource;

      // Create pending event via React state
      this.createPendingEvent(task, context);

      // Format dates using the APPOINTMENT time (not drop position)
      const { startDate, endDate } = formatDatesUsingTimeZone(
        appointmentTime,
        task,
        this.timeZone
      );

      this?.onDropTask?.({
        taskId: task?.data?.extRefId || '',
        resourceId,
        dates: { startDate, endDate },
      });
      context.finalize?.();
    } else {
      // Show toast notification for invalid drops
      if (target && this._lastInvalidReason) {
        this.onInvalidDrop?.(this._lastInvalidReason);
      }
      this.abort();
    }

    // Clean up
    document.body.style.cursor = '';
    this._lastInvalidReason = '';
    this._isValidDrop = false;
    schedule.element?.classList.remove('b-dragging-event');
    if (schedule.features?.eventTooltip) {
      schedule.features.eventTooltip.disabled = false;
    }
    if (schedule.timeAxisSubGrid) {
      schedule.disableScrollingCloseToEdges?.(schedule.timeAxisSubGrid);
    }
  };

  set schedule(schedule: SchedulerPro) {
    this._schedule = schedule as SchedulerDrag;
  }

  get schedule(): SchedulerPro {
    return this._schedule;
  }

  override onAbort = () => {
    const { _schedule: schedule } = this;
    document.body.style.cursor = '';
    this._isValidDrop = false;
    schedule?.unhighlightTimeSpans?.();
    schedule.element?.classList.remove('b-dragging-event');
    if (schedule.features?.eventTooltip) {
      schedule.features.eventTooltip.disabled = false;
    }
    if (schedule.timeAxisSubGrid) {
      schedule.disableScrollingCloseToEdges?.(schedule.timeAxisSubGrid);
    }
    if (this._dragTooltip) {
      this._dragTooltip.destroy();
      this._dragTooltip = undefined;
    }
  };
}

export function useDragDrop({
  schedulerRef,
  gridRef,
  timeZone,
  onDrop,
  enabled = true,
  onPendingEventChange,
  onInvalidDrop,
}: UseDragDropOptions) {
  const dragHelperRef = useRef<Drag | null>(null);

  const cleanup = useCallback(() => {
    if (dragHelperRef.current) {
      dragHelperRef.current.destroy();
      dragHelperRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      cleanup();
      return;
    }

    const scheduler = schedulerRef.current?.instance;
    const grid = gridRef.current?.instance;

    if (!scheduler || !grid) return;

    const setupDrag = () => {
      cleanup();

      const dragHelper = new Drag({
        grid,
        schedule: scheduler,
        outerElement: grid.element,
        timeZone,
        onDropTask: async (values) => {
          try {
            await onDrop({
              taskId: values.taskId,
              resourceId: values.resourceId,
              startDate: values.dates.startDate,
              endDate: values.dates.endDate,
            });
          } catch (error) {
            if (process.env['NODE_ENV'] === 'development') {
              // eslint-disable-next-line no-console
              console.error('[useDragDrop] Drop failed:', error);
            }
          }
        },
        onPendingEventChange,
        onInvalidDrop,
        containers: [grid.element],
      });

      dragHelperRef.current = dragHelper;
    };

    // Delay to ensure DOM elements are ready
    const timeoutId = setTimeout(setupDrag, 100);

    return () => {
      clearTimeout(timeoutId);
      cleanup();
    };
  }, [
    schedulerRef,
    gridRef,
    timeZone,
    onDrop,
    enabled,
    cleanup,
    onPendingEventChange,
    onInvalidDrop,
  ]);

  return { dragHelper: dragHelperRef };
}
